import { memo, useCallback, useState } from 'react';
import { IconHighlight, ModalLayout } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Trash2 } from 'lucide-react';

import { useDialogKeyboard } from '../../hooks/useDialogKeyboard';
import { NO_HEADER_LINE, WIDE_MODAL } from '../shared/modalClasses';
import MembersRemoveStep1 from './MembersRemoveStep1';
import MembersRemoveStep2 from './MembersRemoveStep2';

const TITLE_ID = 'members-remove-title';
const STEP = Object.freeze({ REVIEW: 1, CONFIRM: 2 });

/**
 * The open bulk Remove dialog and its two steps. Mount it only while there is something to
 * remove, so the step and the typed word start fresh each time.
 *
 * @param {object} props
 * @param {{ id: string, displayName: string, email: string }[]} props.members
 * @param {boolean} props.isBusy Removing: Back, the way out and the red button are locked.
 * @param {() => void} props.onConfirm
 * @param {() => void} props.onCancel
 */
const MembersRemoveFlow = ({ members, isBusy, onConfirm, onCancel }) => {
  const { t } = useTranslation('people-access');
  const [step, setStep] = useState(STEP.REVIEW);
  const [typed, setTyped] = useState('');
  const count = members.length;
  const word = t('members.remove.confirmWord');
  const isMatch = typed.trim().toUpperCase() === word.toUpperCase();

  // Esc closes unless the removal is in flight.
  const handleEscape = useCallback(() => {
    if (!isBusy) onCancel();
  }, [isBusy, onCancel]);
  useDialogKeyboard(true, handleEscape, TITLE_ID);

  const handleContinue = useCallback(() => setStep(STEP.CONFIRM), []);
  const handleBack = useCallback(() => setStep(STEP.REVIEW), []);

  return (
    <ModalLayout
      isOpen
      onClose={onCancel}
      useBodyPortal
      title={t('members.remove.title', { count })}
      subtitle={step === STEP.REVIEW ? t('members.remove.step1', { count }) : t('members.remove.step2', { count, word })}
      closeAriaLabel={t('members.remove.close')}
      titleId={TITLE_ID}
      titlePrefix={<IconHighlight icon={Trash2} bgColor="Error" iconColor="Error" size="medium" />}
      panelClassName={NO_HEADER_LINE}
      closeDisabled={isBusy}
      maxWidthClassName={WIDE_MODAL}
    >
      {step === STEP.REVIEW ? (
        <MembersRemoveStep1 members={members} onCancel={onCancel} onContinue={handleContinue} />
      ) : (
        <MembersRemoveStep2
          word={word}
          count={count}
          value={typed}
          isMatch={isMatch}
          isBusy={isBusy}
          onChange={setTyped}
          onBack={handleBack}
          onConfirm={onConfirm}
        />
      )}
    </ModalLayout>
  );
};

export default memo(MembersRemoveFlow);
